import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { V3Logo } from "@/components/ui/v3/logo";
import { cn } from "@/lib/utils";

// Better Auth sign-in screens on the v3.2 tokens: canvas page, one panel,
// pill buttons. The same classes work in light and dark mode.

// 16px text keeps iOS from zooming into the field.
export const AUTH_INPUT =
  "h-12 w-full rounded-xl border-0 bg-v3-surface-2 px-4 text-[16px] text-v3-text shadow-v3-hairline outline-hidden transition-[background-color,box-shadow] placeholder:text-v3-text-3 focus:bg-v3-panel focus:ring-2 focus:ring-v3-brand-ring disabled:opacity-60";
export const AUTH_LABEL = "mb-2 block text-[13px] font-semibold text-v3-text";
export const AUTH_PRIMARY =
  "inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-v3-brand px-5 text-[15px] font-semibold text-v3-on-brand transition-opacity hover:opacity-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring focus-visible:ring-offset-2 focus-visible:ring-offset-v3-panel disabled:pointer-events-none disabled:opacity-50";
export const AUTH_SECONDARY =
  "inline-flex h-12 w-full items-center justify-center gap-2.5 rounded-full bg-v3-panel px-5 text-[15px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:pointer-events-none disabled:opacity-50";
export const AUTH_LINK =
  "rounded-sm font-semibold text-v3-brand-strong underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:pointer-events-none disabled:opacity-50 dark:text-v3-brand";

export const AuthShell = ({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    // Phones: logo in the upper part, card anchored at the bottom so the
    // buttons sit within thumb reach (like the iOS app). From sm up: logo and
    // card as one group, centred, the logo about 32px above the card.
    <div className="flex min-h-dvh w-full flex-col items-center bg-v3-canvas px-4 pt-[max(2rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] font-v3 text-v3-text antialiased sm:justify-center sm:pt-[max(1.5rem,env(safe-area-inset-top))] sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <header className="flex w-full flex-1 flex-col items-center justify-center gap-2 pb-8 sm:w-auto sm:flex-none">
        <V3Logo size={36} />
        <p className="text-[15px] text-v3-text-2 sm:hidden">
          {t("auth.native.tagline")}
        </p>
      </header>
      <main className="w-full max-w-[400px]">
        <div className="rounded-[20px] bg-v3-panel p-5 shadow-v3-hairline sm:p-8">
          <h1 className="font-v3-display text-[24px] font-bold leading-tight tracking-[-0.02em] text-balance sm:text-[26px]">
            {title}
          </h1>
          {description && (
            <p className="mt-2 text-[15px] leading-relaxed text-v3-text-2">
              {description}
            </p>
          )}
          <div className="mt-5 sm:mt-6">{children}</div>
        </div>
        {footer && (
          <div className="mt-4 flex flex-col items-center gap-2 text-center text-[14px] text-v3-text-2 sm:mt-6 sm:gap-3">
            {footer}
          </div>
        )}
      </main>
    </div>
  );
};

// One live region for sign-in feedback, styled by tone.
export const AuthNotice = ({
  tone = "info",
  children,
  className,
}: {
  tone?: "info" | "error" | "success";
  children: ReactNode;
  className?: string;
}): JSX.Element => (
  <p
    className={cn(
      "rounded-xl px-4 py-3 text-[14px] leading-snug",
      tone === "error" && "bg-v3-rec-tint text-v3-rec-text",
      tone === "success" && "bg-v3-ok-tint text-v3-ok-text",
      tone === "info" && "bg-v3-surface-2 text-v3-text-2",
      className,
    )}
    role="status"
  >
    {children}
  </p>
);

export const AuthDivider = ({ label }: { label: string }): JSX.Element => (
  <div className="my-5 flex items-center gap-3" role="presentation">
    <div className="h-px flex-1 bg-v3-border-strong" />
    <span className="text-[12px] font-medium uppercase tracking-[0.06em] text-v3-text-3">
      {label}
    </span>
    <div className="h-px flex-1 bg-v3-border-strong" />
  </div>
);

export const AppleMark = (): JSX.Element => (
  <svg aria-hidden className="size-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M16.37 12.74c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.47.83-.72 0-1.82-.81-2.99-.79-1.54.02-2.96.9-3.75 2.27-1.6 2.78-.41 6.89 1.15 9.14.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.23-.02 2.01-1.12 2.76-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.39-.92-2.4-3.66zM14.1 5.98c.63-.77 1.06-1.83.94-2.89-.91.04-2.01.61-2.66 1.37-.58.67-1.09 1.75-.96 2.79 1.02.08 2.05-.52 2.68-1.27z" />
  </svg>
);

export const GoogleMark = (): JSX.Element => (
  <svg aria-hidden className="size-5" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);
